import { Icon, type IconName } from '@/components/ui/icon';
import { cn } from '@/lib/cn';

export type StatTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger';

const TONES: Record<StatTone, string> = {
  neutral: 'text-content-muted bg-surface-muted border-border',
  primary: 'text-primary bg-primary-soft border-primary/25',
  success: 'text-success bg-success-soft border-success/25',
  warning: 'text-warning bg-warning-soft border-warning/25',
  danger: 'text-danger bg-danger-soft border-danger/25',
};

export interface StatCardProps {
  label: string;
  value: number | string;
  icon?: IconName;
  tone?: StatTone;
  /** Small note under the figure, e.g. "4 expiring soon". */
  hint?: string;
  onClick?: () => void;
}

/**
 * A single dashboard figure. Becomes a button when `onClick` is supplied, so
 * a tile that filters a list is keyboard-reachable rather than a clickable div.
 */
export function StatCard({ label, value, icon, tone = 'neutral', hint, onClick }: StatCardProps) {
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-2xs font-semibold tracking-wide text-content-muted uppercase">{label}</p>
        {icon && (
          <span className={cn('rounded-xs border p-0.5', TONES[tone])}>
            <Icon name={icon} size={14} />
          </span>
        )}
      </div>

      <p className="mt-1.5 text-xl font-bold text-content tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-2xs text-content-subtle">{hint}</p>}
    </>
  );

  /**
   * `flex h-full flex-col` is load-bearing: with an onClick this renders as a
   * <button>, and a button centres its content vertically. Without it, a card
   * with no hint floats in the middle of the row while its taller neighbours
   * sit at the top - visibly out of line across a stat row.
   */
  const className = cn(
    'flex h-full flex-col rounded-sm border border-border bg-surface p-2.5 text-left',
    'shadow-[0_1px_2px_rgba(0,0,0,0.06)]',
    onClick && 'cursor-pointer transition-colors hover:border-border-strong',
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}
